if (!customElements.get('tab-list')) {
  customElements.define(
    'tab-list',
    class TabList extends HTMLUListElement {
      constructor() {
        super();

        this.controls.forEach((button) => button.addEventListener('click', this.handleButtonClick.bind(this)));
      }

      get controls() {
        return this._controls = this._controls || Array.from(this.querySelectorAll('[aria-controls]'));
      }

      handleButtonClick(event) {
        event.preventDefault();

        this.controls.forEach((button) => {
          button.setAttribute('aria-expanded', 'false');

          const panel = document.getElementById(button.getAttribute('aria-controls'));
          panel?.removeAttribute('open');
        });

        const target = event.currentTarget;
        target.setAttribute('aria-expanded', 'true');

        const panel = document.getElementById(target.getAttribute('aria-controls'));
        panel?.setAttribute('open', '');
      }

      reset() {
        const firstControl = this.controls[0];
        firstControl.dispatchEvent(new Event('click'));
      }
    }, { extends: 'ul' }
  );
}

if (!customElements.get('cart-drawer')) {
  customElements.define(
    'cart-drawer',
    class CartDrawer extends DrawerElement {
      constructor() {
        super();

        this.onPrepareBundledSectionsListener = this.onPrepareBundledSections.bind(this);
        this.onCartRefreshListener = this.onCartRefresh.bind(this);
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      get shouldAppendToBody() {
        return false;
      }

      get recentlyViewed() {
        return this.querySelector('recently-viewed');
      }

      get tabList() {
        return this.querySelector('[is="tab-list"]');
      }

      connectedCallback() {
        super.connectedCallback();

        document.addEventListener('cart:bundled-sections', this.onPrepareBundledSectionsListener);
        document.addEventListener('cart:refresh', this.onCartRefreshListener);
        if (this.recentlyViewed) {
          this.recentlyViewed.addEventListener('is-empty', this.onRecentlyViewedEmpty.bind(this));
        }
      }

      disconnectedCallback() {
        super.disconnectedCallback();
    
        document.removeEventListener('cart:bundled-sections', this.onPrepareBundledSectionsListener);
        document.removeEventListener('cart:refresh', this.onCartRefreshListener);
      }

      onPrepareBundledSections(event) {
        event.detail.sections.push(this.sectionId);
      }

      onRecentlyViewedEmpty() {
        this.recentlyViewed.innerHTML = `
        <div class="drawer__scrollable relative flex justify-center items-start grow shrink text-center">
          <div class="drawer__empty grid gap-5 md:gap-8">
            <h2 class="drawer__empty-text heading leading-none tracking-tight">${theme.strings.recentlyViewedEmpty}</h2>
          </div>
        </div>
        `;
      }

      async onCartRefresh(event) {
        const id = `MiniCart-${this.sectionId}`;
        if (document.getElementById(id) === null) return;

        const responseText = await (await fetch(`${theme.routes.root_url}?section_id=${this.sectionId}`)).text();
        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

        document.getElementById(id).innerHTML = parsedHTML.getElementById(id).innerHTML;

        if (event.detail.open === true) {
          this.show();
        }
      }

      show(focusElement = null, animate = true) {
        super.show(focusElement, animate);

        if (this.tabList) {
          this.tabList.reset();

          if (this.open) {
            theme.a11y.trapFocus(this, this.focusElement);
          }
        }
      }
    }
  );
}

// Add-on variant toggle (adds/removes a specific variant and refreshes the drawer)
(() => {
  const VARIANT_ATTR = 'data-cart-addon-variant';
  const OPT_OUT_KEY = 'cart-addon-optout';
  let autoAddonInFlight = false;
  let addonOptOut = false; // tracks if user explicitly unchecked
  let lastCartCount = null; // track previous cart count to detect first-item adds

  function getAddonCheckbox() {
    return document.querySelector(`[${VARIANT_ATTR}]`);
  }

  function getVariantId() {
    return getAddonCheckbox()?.getAttribute(VARIANT_ATTR);
  }

  function setOptOut(variantId, value) {
    if (!variantId) return;
    try {
      const key = `${OPT_OUT_KEY}:${variantId}`;
      if (value) {
        localStorage.setItem(key, '1');
      } else {
        localStorage.removeItem(key);
      }
    } catch (e) {
      console.error(e);
    }
  }

  function getOptOut(variantId) {
    if (!variantId) return false;
    try {
      const key = `${OPT_OUT_KEY}:${variantId}`;
      return localStorage.getItem(key) === '1';
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  function hasOptOutStored(variantId) {
    if (!variantId) return false;
    try {
      const key = `${OPT_OUT_KEY}:${variantId}`;
      return localStorage.getItem(key) !== null;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async function addVariant(variantId) {
    const body = JSON.stringify({
      items: [{ id: Number(variantId), quantity: 1 }]
    });
    const response = await fetch(`${theme.routes.cart_add_url}`, { ...theme.utils.fetchConfig(), ...{ body } });
    const parsed = await response.json();
    if (parsed?.status) {
      throw new Error(parsed?.description || 'Unable to add item');
    }
  }

  async function removeVariant(variantId) {
    const body = JSON.stringify({
      updates: { [variantId]: 0 }
    });
    const response = await fetch(`${theme.routes.cart_update_url}`, { ...theme.utils.fetchConfig(), ...{ body } });
    const parsed = await response.json();
    if (parsed?.status) {
      throw new Error(parsed?.description || 'Unable to remove item');
    }
  }

  async function fetchCart() {
    const response = await fetch(`${theme.routes.cart_url}.js`, theme.utils.fetchConfig('javascript'));
    return response.json();
  }

  function countNonAddonQty(cart, variantId) {
    return cart.items.reduce((sum, item) => {
      return String(item.id) === String(variantId) ? sum : sum + item.quantity;
    }, 0);
  }

  async function syncAddonWithCart(event) {
    const variantId = getVariantId();
    if (!variantId || autoAddonInFlight) return;

    autoAddonInFlight = true;
    try {
      const cart = event?.detail?.cart || await fetchCart();
      const previousCount = lastCartCount;

      // If cart is fully empty, clear opt-out so next first item can auto-add.
      if (cart.item_count === 0) {
        getAddonCheckbox() && (getAddonCheckbox().checked = false);
        addonOptOut = false;
        setOptOut(variantId, false);
      }

      // If cart was empty and now has items, treat as fresh and allow auto-add again.
      if (previousCount === 0 && cart.item_count > 0) {
        addonOptOut = false;
        setOptOut(variantId, false);
      }

      const nonAddonQty = countNonAddonQty(cart, variantId);
      const addonInCart = cart.items.some((item) => String(item.id) === String(variantId));
      const optOutStored = hasOptOutStored(variantId);

      // If no opt-out is stored and the cart has other items, automatically opt-in once.
      if (!optOutStored && nonAddonQty > 0 && !addonInCart) {
        const checkbox = getAddonCheckbox();
        if (checkbox) {
          checkbox.checked = true;
        }
        await addVariant(variantId);
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
        lastCartCount = cart.item_count + 1;
        return;
      }

      // Remove the add-on if it is the only item left.
      if (nonAddonQty === 0 && addonInCart) {
        await removeVariant(variantId);
        getAddonCheckbox() && (getAddonCheckbox().checked = false);
        addonOptOut = false; // reset when cart is empty
        setOptOut(variantId, false);
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
        return;
      }

      // If the add-on product was removed from cart, uncheck the checkbox
      if (!addonInCart) {
        const checkbox = getAddonCheckbox();
        if (checkbox) {
          checkbox.checked = false;
        }
        // If other items remain, treat this as an opt-out
        if (nonAddonQty > 0) {
          addonOptOut = true;
          setOptOut(variantId, true);
        }
      } else {
        // If add-on is in cart, ensure checkbox is checked
        const checkbox = getAddonCheckbox();
        if (checkbox && !checkbox.checked) {
          checkbox.checked = true;
        }
      }

      // update last cart count for next sync
      lastCartCount = cart.item_count;
    } catch (e) {
      console.error(e);
    } finally {
      autoAddonInFlight = false;
    }
  }

  document.addEventListener('change', async (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (!target.hasAttribute(VARIANT_ATTR)) return;

    const variantId = target.getAttribute(VARIANT_ATTR);
    if (!variantId) return;

    const originalChecked = !target.checked ? true : false; // inverse for fallback if failure
    target.setAttribute('disabled', 'true');
    try {
      const cart = await fetchCart();
      const nonAddonQty = countNonAddonQty(cart, variantId);

      // Prevent adding the add-on when no other items are in the cart.
      if (target.checked && nonAddonQty === 0) {
        target.checked = false;
        return;
      }

      if (target.checked) {
        addonOptOut = false;
        setOptOut(variantId, false);
        await addVariant(variantId);
      } else {
        addonOptOut = true;
        setOptOut(variantId, true);
        await removeVariant(variantId);
      }
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
    } catch (e) {
      // revert UI on error
      target.checked = originalChecked;
      console.error(e);
      alert((e && e.message) || 'An error occurred. Please try again.');
    } finally {
      target.removeAttribute('disabled');
    }
  });

  document.addEventListener('cart:updated', syncAddonWithCart);
  document.addEventListener('cart:refresh', syncAddonWithCart);
  // If user removes the add-on via its remove button, immediately mark as opt-out and uncheck
  document.addEventListener('click', (event) => {
    const removeAnchor = event.target?.closest && event.target.closest('a[is="cart-remove-button"]');
    if (!removeAnchor) return;
    const variantId = getVariantId();
    if (!variantId) return;
    const lineItem = removeAnchor.closest('li[data-variant-id]');
    if (!lineItem) return;
    const removedVariantId = lineItem.getAttribute('data-variant-id');
    if (String(removedVariantId) !== String(variantId)) return;
    // User explicitly removed the add-on
    const checkbox = getAddonCheckbox();
    if (checkbox) {
      checkbox.checked = false;
    }
    addonOptOut = true;
    setOptOut(variantId, true);
  }, true);
  document.addEventListener('DOMContentLoaded', () => {
    const variantId = getVariantId();
    addonOptOut = getOptOut(variantId);
    if (addonOptOut && getAddonCheckbox()) {
      getAddonCheckbox().checked = false;
    }
    lastCartCount = null; // allow first sync to capture empty state
    syncAddonWithCart();
  });
})();

if (!customElements.get('cart-remove-button')) {
  customElements.define(
    'cart-remove-button',
    class CartRemoveButton extends HTMLAnchorElement {
      constructor() {
        super();

        this.addEventListener('click', (event) => {
          event.preventDefault();

          const cartItems = this.closest('cart-items');
          cartItems.updateQuantity(this.getAttribute('data-index'), 0);
        });
      }
    }, { extends: 'a' }
  );
}

if (!customElements.get('cart-items')) {
  customElements.define(
    'cart-items',
    class CartItems extends HTMLElement {
      cartUpdateUnsubscriber = undefined;

      constructor() {
        super();

        this.addEventListener('change', theme.utils.debounce(this.onChange.bind(this), 300));
        this.cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      onChange(event) {
        this.validateQuantity(event);
      }

      onCartUpdate(event) {
        if (event.cart.errors) {
          this.onCartError(event.cart.errors, event.target);
          return;
        }

        const sectionToRender = new DOMParser().parseFromString(event.cart.sections[this.sectionId], 'text/html');

        const miniCart = document.querySelector(`#MiniCart-${this.sectionId}`);
        if (miniCart) {
          const updatedElement = sectionToRender.querySelector(`#MiniCart-${this.sectionId}`);
          if (updatedElement) {
            miniCart.innerHTML = updatedElement.innerHTML;
          }
        }

        const mainCart = document.querySelector(`#MainCart-${this.sectionId}`);
        if (mainCart) {
          const updatedElement = sectionToRender.querySelector(`#MainCart-${this.sectionId}`);
          if (updatedElement) {
            mainCart.innerHTML = updatedElement.innerHTML;
          }
          else {
            mainCart.closest('.cart').classList.add('is-empty');
            mainCart.remove();
          }
        }

        const lineItem = document.getElementById(`CartItem-${event.line}`) || document.getElementById(`CartDrawer-Item-${event.line}`);
        if (lineItem && lineItem.querySelector(`[name="${event.name}"]`)) {
          theme.a11y.trapFocus(mainCart || miniCart, lineItem.querySelector(`[name="${event.name}"]`));
        }
        else if (event.cart.item_count === 0) {
          miniCart
            ? theme.a11y.trapFocus(miniCart, miniCart.querySelector('a'))
            : theme.a11y.trapFocus(document.querySelector('.empty-state'), document.querySelector('.empty-state__link'));
        }
        else {
          miniCart
            ? theme.a11y.trapFocus(miniCart, miniCart.querySelector('.horizontal-product__title'))
            : theme.a11y.trapFocus(mainCart, mainCart.querySelector('.cart__item-title'));
        }

        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: {
            cart: event.cart
          }
        }));
      }

      onCartError(errors, target) {
        if (target) {
          // this.updateQuantity(target.getAttribute('data-index'), target.defaultValue, document.activeElement.getAttribute('name'), target);
          this.disableLoading(target.getAttribute('data-index'));
          this.setValidity(target, errors);
          return;
        }
        else {
          window.location.href = theme.routes.cart_url;
        }

        alert(errors);
      }

      updateQuantity(line, quantity, name, target) {
        this.enableLoading(line);

        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

        const body = JSON.stringify({
          id: line,
          quantity,
          sections: sectionsToBundle
        });

        fetch(`${theme.routes.cart_change_url}`, { ...theme.utils.fetchConfig(), ...{ body } })
          .then((response) => response.json())
          .then((parsedState) => {
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cart: parsedState, target, line, name });
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error(error);
            }
          });
      }

      enableLoading(line) {
        const loader = document.getElementById(`Loader-${this.sectionId}-${line}`);
        if (loader) loader.hidden = false;
      }

      disableLoading(line) {
        const loader = document.getElementById(`Loader-${this.sectionId}-${line}`);
        if (loader) loader.hidden = true;
      }

      setValidity(target, message) {
        target.setCustomValidity(message);
        target.reportValidity();
        target.value = target.defaultValue;
        target.select();
      }

      validateQuantity(event) {
        const target = event.target;
        const inputValue = parseInt(target.value);
        const index = target.getAttribute('data-index');
        let message = '';

        if (inputValue < parseInt(target.getAttribute('data-min'))) {
          message = theme.quickOrderListStrings.minError.replace('[min]', target.getAttribute('data-min'));
        }
        else if (inputValue > parseInt(target.max)) {
          message = theme.quickOrderListStrings.maxError.replace('[max]', target.max);
        }
        else if (inputValue % parseInt(target.step) !== 0) {
          message = theme.quickOrderListStrings.stepError.replace('[step]', target.step);
        }

        if (message) {
          this.setValidity(target, message);
        }
        else {
          target.setCustomValidity('');
          target.reportValidity();
          this.updateQuantity(index, inputValue, document.activeElement.getAttribute('name'), target);
        }
      }
    }
  );
}

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener('change', theme.utils.debounce(this.onChange.bind(this), 300));
      }

      onChange(event) {
        const body = JSON.stringify({ note: event.target.value });
        fetch(`${theme.routes.cart_update_url}`, { ...theme.utils.fetchConfig(), ...{ body } });
      }
    }
  );
}

if (!customElements.get('main-cart')) {
  customElements.define(
    'main-cart',
    class MainCart extends HTMLElement {
      constructor() {
        super();

        document.addEventListener('cart:bundled-sections', this.onPrepareBundledSections.bind(this));
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      onPrepareBundledSections(event) {
        event.detail.sections.push(this.sectionId);
      }
    }
  );
}

if (!customElements.get('country-province')) {
  customElements.define(
    'country-province',
    class CountryProvince extends HTMLElement {
      constructor() {
        super();

        this.provinceElement = this.querySelector('[name="address[province]"]');
        this.countryElement = this.querySelector('[name="address[country]"]');
        this.countryElement.addEventListener('change', this.handleCountryChange.bind(this));

        if (this.getAttribute('country') !== '') {
          this.countryElement.selectedIndex = Math.max(0, Array.from(this.countryElement.options).findIndex((option) => option.textContent === this.getAttribute('data-country')));
          this.countryElement.dispatchEvent(new Event('change'));
        }
        else {
          this.handleCountryChange();
        }
      }

      handleCountryChange() {
        const option = this.countryElement.options[this.countryElement.selectedIndex], provinces = JSON.parse(option.getAttribute('data-provinces'));
        this.provinceElement.parentElement.hidden = provinces.length === 0;

        if (provinces.length === 0) {
          return;
        }

        this.provinceElement.innerHTML = '';

        provinces.forEach((data) => {
          const selected = data[1] === this.getAttribute('data-province');
          this.provinceElement.options.add(new Option(data[1], data[0], selected, selected));
        });
      }
    }
  );
}

if (!customElements.get('shipping-calculator')) {
  customElements.define(
    'shipping-calculator',
    class ShippingCalculator extends HTMLFormElement {
      constructor() {
        super();

        this.onSubmitHandler = this.onSubmit.bind(this);
      }

      connectedCallback() {
        this.submitButton = this.querySelector('[type="submit"]');
        this.resultsElement = this.lastElementChild;

        this.submitButton.addEventListener('click', this.onSubmitHandler);
      }

      disconnectedCallback() {
        this.submitButton.removeEventListener('click', this.onSubmitHandler);
      }

      onSubmit(event) {
        event.preventDefault();

        this.abortController?.abort();
        this.abortController = new AbortController();

        const zip = this.querySelector('[name="address[zip]"]').value,
          country = this.querySelector('[name="address[country]"]').value,
          province = this.querySelector('[name="address[province]"]').value;

        this.submitButton.setAttribute('aria-busy', 'true');

        const body = JSON.stringify({
          shipping_address: { zip, country, province }
        });
        let sectionUrl = `${theme.routes.cart_url}/shipping_rates.json`;

        // remove double `/` in case shop might have /en or language in URL
        sectionUrl = sectionUrl.replace('//', '/');

        fetch(sectionUrl, { ...theme.utils.fetchConfig('javascript'), ...{ body }, signal: this.abortController.signal })
          .then((response) => response.json())
          .then((parsedState) => {
            if (parsedState.shipping_rates) {
              this.formatShippingRates(parsedState.shipping_rates);
            }
            else {
              this.formatError(parsedState);
            }
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error(error);
            }
          })
          .finally(() => {
            this.resultsElement.hidden = false;
            this.submitButton.removeAttribute('aria-busy');
          });
      }

      formatError(errors) {
        const shippingRatesList = Object.keys(errors).map((errorKey) => {
          return `<li>${errors[errorKey]}</li>`;
        });
        this.resultsElement.innerHTML = `
          <div class="alert alert--error grid gap-2 text-sm leading-tight">
            <p>${theme.shippingCalculatorStrings.error}</p>
            <ul class="list-disc grid gap-2" role="list">${shippingRatesList.join('')}</ul>
          </div>
        `;
      }

      formatShippingRates(shippingRates) {
        const shippingRatesList = shippingRates.map(({ presentment_name, currency, price }) => {
          return `<li>${presentment_name}: ${currency} ${price}</li>`;
        });
        this.resultsElement.innerHTML = `
          <div class="alert alert--${shippingRates.length === 0 ? 'error' : 'success'} grid gap-2 text-sm leading-tight">
            <p>${shippingRates.length === 0 ? theme.shippingCalculatorStrings.notFound : shippingRates.length === 1 ? theme.shippingCalculatorStrings.oneResult : theme.shippingCalculatorStrings.multipleResults}</p>
            ${shippingRatesList === '' ? '' : `<ul class="list-disc grid gap-2" role="list">${shippingRatesList.join('')}</ul>`}
          </div>
        `;

      }
    }, { extends: 'form' }
  );
}
